import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/skip-comparison", label: "Skip Comparison" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <>
      <header
        className="sticky top-0 z-40 bg-white border-b border-gray-200"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-32">
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <Logo size={120} />
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition ${
                    isActive
                      ? "bg-black text-white"
                      : "text-gray-700 hover:text-black hover:bg-gray-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/book" className="wa-btn-primary ml-2 text-sm" data-testid="header-book-button">
              Book Collection
            </Link>
          </nav>

          <button
            className="md:hidden p-2 rounded-lg border border-gray-300 text-black"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-2 rounded-lg font-semibold ${
                    loc.pathname === n.to
                      ? "bg-black text-white"
                      : "text-gray-800 hover:bg-gray-100"
                  }`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/book"
                onClick={() => setOpen(false)}
                className="wa-btn-primary justify-center mt-2"
                data-testid="mobile-book-button"
              >
                Book Collection
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[70vh] bg-black">
        <Outlet />
      </main>

      <footer className="border-t border-[#1a1a1c] bg-black mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="flex items-center">
              <Logo size={64} />
            </Link>
            <p className="text-sm text-gray-400 mt-4 max-w-xs">
              Fair UK rubbish removal, priced by weight. Pay only for what we take.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Services</div>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/book" className="hover:text-[#8DC63F]">Book a collection</Link></li>
              <li><Link to="/pricing" className="hover:text-[#8DC63F]">Pricing</Link></li>
              <li><Link to="/skip-comparison" className="hover:text-[#8DC63F]">Skip vs Weigh Away</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Company</div>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/faq" className="hover:text-[#8DC63F]">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-[#8DC63F]">Contact</Link></li>
              <li><Link to="/admin/login" className="hover:text-[#8DC63F]" data-testid="footer-admin-link">Admin</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Trust</div>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>Licensed waste carrier</li>
              <li>Fair, transparent pricing</li>
              <li>UK based, mobile-first</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#1a1a1c] py-5 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} Weigh Away. All prices in GBP.
        </div>
      </footer>
    </>
  );
}
